import axios from "axios";
import { isNativeApp } from "../utils/platform";

const BASE_URL = (process.env.REACT_APP_API_URL || "http://localhost:4000/api").replace(/\/$/, "");

const API = axios.create({ baseURL: BASE_URL });

// ── Request: attach access token ──────────────────────────────────────────────
API.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken") || localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Le dice al backend que omita la verificación de reCAPTCHA (no funciona en el
  // WebView nativo — ver utils/platform.js)
  if (isNativeApp) config.headers["X-Client-Platform"] = "capacitor";
  return config;
});

// ── Response: handle 401 with automatic token refresh ────────────────────────
let isRefreshing = false;
let failedQueue = [];

function processQueue(error, token = null) {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  failedQueue = [];
}

const PUBLIC_PATHS = ["/login", "/register", "/register-business", "/add-company", "/forgot-password", "/reset-password"];

function clearSession() {
  localStorage.removeItem("token");
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("user");
  const isPublic = PUBLIC_PATHS.some((p) => window.location.pathname.startsWith(p));
  if (!isPublic) window.location.href = "/login";
}

const AUTH_ROUTES = ["/auth/login", "/auth/register", "/auth/google", "/auth/refresh"];

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const url = originalRequest?.url || "";
    const isAuthRoute = AUTH_ROUTES.some((r) => url.includes(r));

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthRoute) {
      const refreshToken = localStorage.getItem("refreshToken");

      // No refresh token — clear session and redirect to login
      if (!refreshToken) {
        clearSession();
        return Promise.reject(error);
      }

      // Queue requests that arrive while a refresh is in progress
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return API(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const res = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken });
        // Backend returns { data: { tokens: { accessToken, refreshToken } } }
        const tokens = res.data?.data?.tokens ?? res.data?.data ?? res.data;
        const newToken = tokens?.accessToken ?? tokens?.token;
        const newRefresh = tokens?.refreshToken;

        if (!newToken) throw new Error("No access token in refresh response");

        localStorage.setItem("accessToken", newToken);
        if (newRefresh) localStorage.setItem("refreshToken", newRefresh);
        API.defaults.headers.common.Authorization = `Bearer ${newToken}`;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        processQueue(null, newToken);
        return API(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearSession();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default API;
